import type {
  Template,
  TemplateDialogPresentation,
  TemplatePromotionPresentationConfig,
  TemplatePublicChromeStyle,
  TemplatePublicShellConfig,
} from '@/app/dashboard/templates/components/types/template';

/**
 * THE canonical customer outer shell, shared by EVERY template type — flipbook,
 * legacy shopfront and platform default alike.
 *
 * This is the unification of the three family shells into one design:
 *  - STRUCTURE from the shopfront templates: the full floating-glass frame —
 *    header, session ribbon, scroll region and bottom rail all visible, because
 *    hidden chrome leaves no affordance for search, account or the Order tab.
 *  - TEXTURE from the flipbook templates: sheet-style dialog presentations —
 *    clean book-like cards for the item detail and ordering sheets instead of
 *    heavy centered modals.
 *  - CONSISTENCY from the platform defaults: the footer stays visible on every
 *    engine and the promotion fallback is pinned to one canonical slot so a
 *    claim never appears in a different place depending on the template.
 *
 * The template still owns its BODY — palette, fonts, card & typography styles,
 * storefront layout, its own promotion nodes. It no longer owns the shell.
 */
export const CUSTOMER_UNIFIED_SHELL_CONFIG: TemplatePublicShellConfig = {
  headerVisible: true,
  actionsVisible: true,
  navigationVisible: true,
  footerVisible: true,
  chromeStyle: 'floating-glass',
  footerStyle: 'visible',
  promotionFallbackPlacement: 'after-template',
  feedbackDialogStyle: 'sheet',
  orderingDialogStyle: 'sheet',
  tableSessionBannerStyle: 'compact',
};

function readTemplateMetaConfig(template?: Template | null) {
  const meta = (template?.meta || {}) as Record<string, unknown>;
  const shell = (meta.publicShell || meta.shell || {}) as Record<string, unknown>;
  const promotions = (meta.promotionPresentation || meta.promotions || {}) as Record<string, unknown>;
  return { shell, promotions };
}

function asRecord(value: unknown) {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function resolveTemplatePublicShellConfig(template?: Template | null): TemplatePublicShellConfig {
  const { shell } = readTemplateMetaConfig(template);
  const pagedFlip = asRecord(template?.pagedFlip);

  return {
    headerVisible: shell.headerVisible === false ? false : true,
    actionsVisible: shell.actionsVisible === false ? false : true,
    navigationVisible: shell.navigationVisible === false ? false : true,
    footerVisible: shell.footerVisible === false ? false : true,
    chromeStyle: (shell.chromeStyle as TemplatePublicChromeStyle) || 'floating-glass',
    footerStyle: (shell.footerStyle as TemplatePublicShellConfig['footerStyle']) || (shell.footerVisible === false ? 'hidden' : 'visible'),
    promotionFallbackPlacement:
      (shell.promotionFallbackPlacement as TemplatePublicShellConfig['promotionFallbackPlacement']) ||
      ((pagedFlip.promotionPages as { enabled?: boolean } | undefined)?.enabled ? 'after-template' : 'hidden'),
    feedbackDialogStyle: (shell.feedbackDialogStyle as TemplateDialogPresentation) || 'sheet',
    orderingDialogStyle: (shell.orderingDialogStyle as TemplateDialogPresentation) || 'sheet',
    tableSessionBannerStyle: (shell.tableSessionBannerStyle as TemplatePublicShellConfig['tableSessionBannerStyle']) || 'compact',
  };
}

export function resolveTemplatePromotionPresentationConfig(
  template?: Template | null,
): Required<TemplatePromotionPresentationConfig> {
  const { promotions } = readTemplateMetaConfig(template);

  return {
    variant: (promotions.variant as Required<TemplatePromotionPresentationConfig>['variant']) || 'compact-strip',
    density: (promotions.density as Required<TemplatePromotionPresentationConfig>['density']) || 'comfortable',
    emphasis: (promotions.emphasis as Required<TemplatePromotionPresentationConfig>['emphasis']) || 'balanced',
    showBadge: promotions.showBadge !== false,
    showDescription: promotions.showDescription !== false,
    showTarget: promotions.showTarget !== false,
    showTiming: promotions.showTiming !== false,
    showClaim: promotions.showClaim !== false,
  };
}

export function isChromeFloating(style: TemplatePublicChromeStyle) {
  return style === 'floating-glass';
}

export function footerVisible(style?: TemplatePublicShellConfig['footerStyle']) {
  return style !== 'hidden';
}

export function promotionFallbackVisible(
  placement?: TemplatePublicShellConfig['promotionFallbackPlacement'],
) {
  return placement !== 'hidden';
}

export function dialogPresentationClassName(presentation?: TemplateDialogPresentation) {
  if (presentation === 'fullscreen') return 'w-full max-w-4xl sm:min-h-[80vh]';
  if (presentation === 'modal') return 'max-w-2xl';
  return 'max-w-2xl sm:max-w-3xl sm:rounded-3xl';
}

export function orderingDialogPresentationClassName(presentation?: TemplateDialogPresentation) {
  if (presentation === 'fullscreen') return 'w-full max-w-5xl sm:min-h-[85vh]';
  if (presentation === 'modal') return 'max-w-3xl';
  return 'max-w-4xl sm:rounded-3xl';
}
